import Highcharts from "highcharts";

/**
 * Minigráfico de área do "Gráfico USDA" e o código de incorporação dele.
 *
 * Compartilhado entre a projeção PSD (USDA.js) e os relatórios NASS
 * (USDANass.js) para os dois saírem com a mesma cara nas matérias. As
 * opções extras são todas opcionais — sem elas o resultado é o de sempre:
 *   fonte          texto da linha "Fonte: …" (padrão RURAL BUSINESS/USDA)
 *   periodoTitulo  trecho entre parênteses no título (padrão "2021 a 2026");
 *                  string vazia tira os parênteses
 *   linhaExtra     terceira linha do título, menor — onde os relatórios NASS
 *                  põem o período, que é longo demais para caber na primeira
 *                  ("acumulado jan-mar, 2021 a 2026") sem quebrar e sobrepor
 *                  a linha de baixo
 *   formatarValor  formatador do rótulo de cada ponto (padrão: pela unidade PSD)
 * e, no ponto, `rotulo` — texto já formatado, que o HTML incorporado usa no
 * lugar da formatação por unidade (que só conhece as unidades do PSD).
 */

const NUM_SAFRAS = 6;

export function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Medida de cada mercado do PSD, no padrão dos gráficos da casa: o número
 * limpo no rótulo e a grandeza no título — "Rebanho Bovino (milhão/cab)"
 * com 943,77, e não "(cabeças)" com "943,77 mi".
 *
 * O PSD manda quase tudo "em mil" ("(1000 HEAD)", "(1000 MT)", "(1000 HA)",
 * "(1000 60 KG BAGS)", "1000 480 lb. Bales"; carnes em "(1000 MT CWE)",
 * equivalente-carcaça). Passando de 1 milhão a escala sobe para milhão.
 *
 * @param unit       unidade como vem do PSD
 * @param maiorValor maior valor da série, na unidade do PSD
 * @returns { divisor, unidade, decimais, sufixo }  — valor exibido = v / divisor
 */
export function medidaUsda(unit, maiorValor = 0) {
  const u = String(unit || "").toLowerCase();
  const milhao = Math.abs(Number(maiorValor) || 0) >= 1000;
  const emMil = (unidadeMilhao, unidadeMil) =>
    milhao
      ? { divisor: 1000, unidade: unidadeMilhao, decimais: 2, sufixo: "" }
      : { divisor: 1, unidade: unidadeMil, decimais: 2, sufixo: "" };

  if (u.includes("percent")) return { divisor: 1, unidade: "%", decimais: 2, sufixo: "%" };
  if (u.includes("ratio")) return { divisor: 1, unidade: "razão", decimais: 2, sufixo: "" };
  if (u.includes("mt/ha")) return { divisor: 1, unidade: "t/ha", decimais: 2, sufixo: "" };
  if (u.includes("kg/ha")) return { divisor: 1, unidade: "kg/ha", decimais: 0, sufixo: "" };
  if (u.includes("head")) return emMil("milhão/cab", "mil/cab");
  if (u.includes("60 kg")) return emMil("milhão de sacas", "mil sacas");
  if (u.includes("480")) return emMil("milhão de fardos", "mil fardos");
  if (u.includes("cwe")) return emMil("milhão/t eq. carcaça", "mil/t eq. carcaça");
  if (u.includes("1000 ha")) return emMil("milhão/ha", "mil/ha");
  if (u.includes("1000 mt")) return emMil("milhão/t", "mil/t");
  return { divisor: 1, unidade: String(unit || "").replace(/[()]/g, "").trim(), decimais: 2, sufixo: "" };
}

/** Valor já na medida do mercado, no padrão brasileiro ("943,77"). */
export function formatarNaMedida(valor, medida) {
  if (valor == null) return "";
  return (
    Highcharts.numberFormat(Number(valor) / medida.divisor, medida.decimais, ",", ".") + (medida.sufixo || "")
  );
}

/**
 * Spec do Gerador de imagens para UM gráfico do USDA, no formato das
 * imagens publicadas ("Rebanho Bovino - Mundial", "Projeção USDA
 * (milhão/cab)", caixa "1 ano / 5 anos" e o ano e o valor em cada ponto):
 * o modelo "Linha com rótulos".
 *
 * @param embedData o mesmo objeto do código incorporado (pontos já em ordem)
 * @param medida    medidaUsda da série ({ divisor: 1, ... } se os pontos já
 *                  vêm na escala, como nos relatórios NASS)
 * @param subtitulo texto antes da unidade (padrão "<atributo> - Projeção USDA")
 */
export function specUsdaParaGerador(embedData, medida, subtitulo) {
  const pontos = embedData.sortedPoints || [];
  const valores = pontos.map((p) => Number((Number(p.valor) / medida.divisor).toFixed(medida.decimais)));
  const n = valores.length;
  const periodos = [];
  if (n >= 2) periodos.push({ rotulo: "1 ano:", serie: 0, de: n - 2, para: n - 1, decimais: 1 });
  const anos = n >= 2 ? pontos[n - 1].anoNum - pontos[0].anoNum : 0;
  if (anos > 1) periodos.push({ rotulo: `${anos} anos:`, serie: 0, de: 0, para: n - 1, decimais: 1 });

  return {
    modelo: "linha-rotulada",
    titulo: [{ texto: `${embedData.nomeMercado} - ${embedData.local}`, cor: "#F0F000" }],
    subtitulo: [
      { texto: `${subtitulo || `${embedData.atributo} - Projeção USDA`} `, cor: "#FFFFFF" },
      { texto: `(${medida.unidade})`, cor: "#FFFFFF", tamanho: 8 },
    ],
    fonte: `Fonte: ${embedData.fonte || "RURAL BUSINESS/USDA"}`,
    fundo: "#000000",
    fundoPlot: "nenhum",
    dados: {
      categorias: pontos.map((p) => String(p.safra)),
      series: [{ nome: embedData.atributo, cor: "#FFC000", corFim: "#FFC000", valores }],
    },
    legenda: { visivel: false },
    valores: { decimais: medida.decimais, prefixo: "", sufixo: medida.sufixo || "", exibirEm: "todos" },
    rotulo: { corTexto: "#000000", corFundo: "#FFFFFF", corBorda: "#000000" },
    extras: {
      mostrarData: true,
      variacoes: periodos.length
        ? [
            {
              periodos,
              seta: "nenhuma",
              em: { gx: 0.5, gy: 0.3 },
              tamanho: 11,
              escalaSeta: 1,
              corAutomatica: false,
              corTexto: "#FFFFFF",
              corFundo: "#000000",
              corBorda: "#C00000",
              larguraBorda: 2,
            },
          ]
        : [],
    },
  };
}

/**
 * Encaixa o título HTML na largura do gráfico (que agora acompanha a
 * coluna): o bloco ocupa a largura toda, centralizado, e a primeira linha
 * diminui a letra até caber (mínimo 10px). Roda a cada desenho, inclusive
 * quando a coluna muda de tamanho.
 */
function ajustarTituloNaLargura() {
  const caixa = this.container?.querySelector(".highcharts-title");
  const bloco = caixa?.firstElementChild;
  const linha = bloco?.firstElementChild;
  if (!caixa || !bloco || !linha) return;
  const largura = this.chartWidth - 12;
  caixa.style.left = "6px";
  bloco.style.width = `${largura}px`;
  let tamanho = 16;
  linha.style.fontSize = `${tamanho}px`;
  while (linha.scrollWidth > largura && tamanho > 10) {
    tamanho -= 1;
    linha.style.fontSize = `${tamanho}px`;
  }
}

/** Variação no padrão das imagens da casa: "-1,3%". */
function formatarPct(v) {
  return Highcharts.numberFormat(v, 1, ",", ".") + "%";
}

// ---------- Gerar opções Highcharts para um mini-gráfico ----------
export function buildMiniChartOptions(points, nomeMercado, atributoTrad, localLabel, unitLabel, color = "#39FF14", opcoes = {}) {
  if (!points.length) return null;

  const sorted = [...points].sort((a, b) => a.anoNum - b.anoNum);
  const last = sorted;

  const rawUnit = (points[0]?.unit || "").toLowerCase();
  // Toda unidade "em mil" (t, ha, cabeças, sacas de 60 kg, fardos). Sacas e
  // fardos ficavam de fora e saíam "63,00" para 63 milhões de sacas.
  const is1000Unit = /^\(?1000 /.test(rawUnit.trim());
  const isPercentUnit = rawUnit.includes("percent");
  // kg/ha (algodão) é número inteiro: 1.688, e não "1,69", que parece t/ha.
  const isKgHa = rawUnit.includes("kg/ha");

  const safras = last.map((p) => p.safra);
  const values = last.map((p) => p.valorFinal);

  // Y-axis range — amplitude real dos dados para maior variação visual
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const spread = (maxVal - minVal) || Math.abs(minVal) * 0.1 || 1;
  const yMin = minVal - spread * 0.15;
  const yMax = maxVal + spread * 0.15;

  // Variações
  const curr = values[values.length - 1] || 0;
  const prev = values.length >= 2 ? values[values.length - 2] : null;
  const yearSpan = last.length >= 2 ? last[last.length - 1].anoNum - last[0].anoNum : 0;
  const old5 = last.length >= 2 && yearSpan > 1 ? values[0] : null;

  const var1 = prev && prev !== 0 ? ((curr - prev) / prev) * 100 : null;
  const var5 = old5 && old5 !== 0 ? ((curr - old5) / old5) * 100 : null;

  const isLastNeg = var1 !== null && var1 < 0;

  const fmtPct = (v) => (v == null ? "—" : formatarPct(v));

  // Subtítulo com variações
  const subtitleParts = [];
  if (var1 !== null) subtitleParts.push("1 ano: " + fmtPct(var1));
  if (var5 !== null) subtitleParts.push(yearSpan + " anos: " + fmtPct(var5));
  const subtitleLine = subtitleParts.length
    ? "  •  " + subtitleParts.join("  •  ") + "  •  "
    : "";

  const fmtVal = opcoes.formatarValor || ((v) => {
    if (v == null) return "";
    if (isPercentUnit) return Highcharts.numberFormat(v, 2, ",", ".") + "%";
    if (isKgHa) return Highcharts.numberFormat(v, 0, ",", ".");
    if (is1000Unit) {
      const real = v * 1000;
      if (real >= 1000000) return Highcharts.numberFormat(v / 1000, 2, ",", ".") + " mi";
      return Highcharts.numberFormat(v, 0, ",", ".") + " mil";
    }
    const abs = Math.abs(v);
    if (abs >= 1000000) return Highcharts.numberFormat(v / 1000000, 2, ",", ".") + "M";
    if (abs >= 1000) return Highcharts.numberFormat(v / 1000, 2, ",", ".");
    if (abs >= 1) return Highcharts.numberFormat(v, 2, ",", ".");
    return Highcharts.numberFormat(v, 2, ",", ".");
  });

  const periodoTitulo = opcoes.periodoTitulo ?? `${sorted[0].anoNum} a ${sorted[sorted.length - 1].anoNum}`;
  const fonte = opcoes.fonte || "RURAL BUSINESS/USDA";
  const linhaTitulo = periodoTitulo ? `${nomeMercado} - ${atributoTrad} (${periodoTitulo})` : `${nomeMercado} - ${atributoTrad}`;
  const alturaLinhaExtra = opcoes.linhaExtra ? 16 : 0;
  // Títulos longos ("Números de animais, Gado - Estoque Final (Ending
  // Stocks) (2022 a 2026)") diminuem a letra para caber numa linha, em vez
  // de vazar dos lados; o tamanho final sai da largura real em render.
  const tamanhoTitulo = 16;

  return {
    chart: {
      type: "areaspline",
      backgroundColor: "#0d0d0d",
      // Sem largura fixa: o gráfico ocupa a coluna. Com 500px fixos ele era
      // cortado em colunas menores (tela com o filtro aberto), escondendo o
      // título e o último ano — justamente o da projeção.
      height: 300,
      spacing: [22, 6, 8, 6],
      style: { fontFamily: "Arial, sans-serif"},
      events: { render: ajustarTituloNaLargura },
    },
    title: {
      text:
        `<div style="display:flex;flex-direction:column;align-items:center;gap:1px;line-height:1.1;text-align:center;">` +
        `<span style="font-size:${tamanhoTitulo}px;font-weight:700;color:#e0e0e0;letter-spacing:0.2px;white-space:nowrap;">${linhaTitulo}</span>` +
        `<span style="font-size:17px;font-weight:600;color:#ffffff;">${localLabel} <span style="font-size:13px;font-weight:400;">(${unitLabel})</span></span>` +
        (opcoes.linhaExtra ? `<span style="font-size:12px;font-weight:600;color:#cfcfcf;">${opcoes.linhaExtra}</span>` : "") +
        `</div>`,
      useHTML: true,
      align: "center",
      y: 4,
    },
    subtitle: {
      text:
        `<div style="text-align:center;margin-bottom:1px;"><span style="font-size:10px;color:#ffffff;letter-spacing:0.2px;">Fonte: ${fonte}</span></div>` +
        (subtitleLine
          ? `<div style="text-align:center;margin-top:6px;"><span style="font-size:12px;font-weight:600;color:#ffffff;letter-spacing:0.2px;">${subtitleLine}</span></div>`
          : ""),
      useHTML: true,
      align: "center",
      y: 42 + alturaLinhaExtra,
    },
    xAxis: {
      categories: safras,
      visible: false,
    },
    yAxis: {
      visible: false,
      min: yMin,
      max: yMax,
    },
    legend: { enabled: false },
    tooltip: { enabled: false },
    plotOptions: {
      areaspline: {
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, hexToRgba(color, 0.35)],
            [1, hexToRgba(color, 0.02)],
          ],
        },
        lineWidth: 2,
        lineColor: color,
        marker: {
          enabled: true,
          radius: 4,
          fillColor: color,
          lineWidth: 0,
        },
        dataLabels: {
          enabled: true,
          useHTML: true,
          allowOverlap: true,
          crop: false,
          overflow: "allow",
          formatter: function () {
            const idx = this.point.index;
            const isLast = idx === values.length - 1;
            const val = fmtVal(this.y);
            const saf = safras[idx];
            if (isLast) {
              const bgAlpha = isLastNeg ? "rgba(192,57,43,0.75)" : "rgba(39,174,96,0.75)";
              return (
                `<div style="text-align:center;background:${bgAlpha};color:#fff;padding:2px 5px;border-radius:3px;font-size:11px;line-height:1.2;">` +
                `<b>${saf}</b><br/>${val}</div>`
              );
            }
            return (
              `<div style="text-align:center;color:#ffffff;font-size:10px;line-height:1.2;">` +
              `<b>${saf}</b><br/>${val}</div>`
            );
          },
          y: -8,
          style: { textOutline: "none" },
        },
      },
    },
    series: [{ data: values, color }],
    credits: { enabled: false },
  };
}

// ---------- Gerador de HTML incorporável ----------
export function generateEmbedHtml(charts, chartWidth = 500, chartHeight = 300) {
  if (!charts.length) return '';

  const chartsData = charts
    .filter((mc) => mc.embedData && mc.embedData.sortedPoints.length)
    .map((mc, i) => ({
      id: `rb-usda-chart-${i}`,
      nomeMercado: mc.embedData.nomeMercado,
      atributo: mc.embedData.atributo,
      local: mc.embedData.local,
      unit: mc.embedData.unit,
      color: mc.embedData.color || '#39FF14',
      pts: mc.embedData.sortedPoints,
      // Só presentes quando informados — o JSON do PSD continua o mesmo.
      fonte: mc.embedData.fonte,
      unidadeTexto: mc.embedData.unidadeTexto,
      linhaExtra: mc.embedData.linhaExtra,
    }));

  if (!chartsData.length) return '';

  const mobileChartHeight = Math.round(chartHeight * 0.86);
  // +24 = padding:12px do .rb-usda-wrap (12 em cima + 12 embaixo). Medido ao
  // vivo com o HTML real gerado: altura de conteúdo = chartHeight + 24 exatos
  // (era +40 antes, sobrando 16px de espaço em branco embaixo do gráfico).
  const desktopHeight = chartHeight + 24; // 1 linha (desktop/tablet largo)
  const mobileStackedHeight =
    chartsData.length * mobileChartHeight + (chartsData.length - 1) * 12 + 24; // N linhas empilhadas (mobile)
  const iframeHeight = Math.max(desktopHeight, mobileStackedHeight); // sempre cabe nos dois layouts, sem depender de script externo
  const iframeWidth = chartsData.length * chartWidth + (chartsData.length - 1) * 12 + 34; // charts + gaps + padding + 10px extra

  const divsHtml = chartsData
    .map((c) => `<div id="${c.id}" class="rb-usda-chart-box" style="height:${chartHeight}px;"></div>`)
    .join('');

  const chartsJson = JSON.stringify(chartsData)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");

  const embedDocument = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { width:100%; overflow-y:auto; }
    .rb-usda-wrap { display:flex; flex-wrap:wrap; gap:12px; padding:12px; width:100%; }
    .rb-usda-chart-box { flex: 1 1 ${chartWidth}px; min-width: 240px; max-width: 100%; }
    @media (max-width: 560px) {
      .rb-usda-chart-box { flex-basis: 100%; max-width: 100%; height: ${mobileChartHeight}px !important; }
    }
  </style>
</head>
<body>
  <div class="rb-usda-wrap" id="rb-usda-wrap">${divsHtml}</div>
  <script id="rb-usda-data" type="application/json">${chartsJson}</script>
  <script src="https://web.ruralbusiness.com.br/highcharts.js"></script>
  <script>
    (function () {
      var chartsNode = document.getElementById('rb-usda-data');
      if (!chartsNode || typeof Highcharts === 'undefined') return;

      var charts = JSON.parse(chartsNode.textContent || '[]');
      var numSafras = ${NUM_SAFRAS};
      var isMobile = window.matchMedia('(max-width: 560px)').matches;
      var renderedCharts = [];

      function fmtVal(value, unit) {
        var raw = (unit || '').toLowerCase();
        var is1000 = /^\\(?1000 /.test(raw.trim());
        var isPct = raw.indexOf('percent') !== -1;
        if (isPct) return value.toFixed(2).replace('.', ',') + '%';
        if (raw.indexOf('kg/ha') !== -1) return Math.round(value).toLocaleString('pt-BR');
        if (is1000) {
          var real = value * 1000;
          if (real >= 1000000) return (value / 1000).toFixed(2).replace('.', ',') + ' mi';
          return Math.round(value) + ' mil';
        }
        var abs = Math.abs(value);
        if (abs >= 1000000) return (value / 1000000).toFixed(2).replace('.', ',') + ' M';
        if (abs >= 1000) return (value / 1000).toFixed(2).replace('.', ',');
        if (abs >= 1) return value.toFixed(2).replace('.', ',');
        return value.toFixed(2).replace('.', ',');
      }

      function fmtPct(value) {
        if (value == null) return '—';
        return (value >= 0 ? '+' : '') + value.toFixed(1).replace('.', ',') + '%';
      }

      function unitLabel(unit) {
        if (!unit) return '';
        var low = unit.toLowerCase();
        if (low.indexOf('1000 mt') !== -1) return 'tonelada';
        if (low.indexOf('1000 ha') !== -1) return 'hectare';
        if (low.indexOf('mt/ha') !== -1) return 't/ha';
        if (low.indexOf('1000 hea') !== -1) return 'cabeças';
        if (low.indexOf('480') !== -1) return 'fardos';
        if (low.indexOf('60 kg') !== -1) return 'sacas de 60 kg';
        if (low.indexOf('kg/ha') !== -1) return 'kg/ha';
        if (low.indexOf('percent') !== -1) return '%';
        return unit;
      }

      function escapeHtml(value) {
        return String(value == null ? '' : value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');
      }

      function hexToRgba(hex, a) {
        var r=parseInt(hex.slice(1,3),16), g=parseInt(hex.slice(3,5),16), b=parseInt(hex.slice(5,7),16);
        return 'rgba('+r+','+g+','+b+','+a+')';
      }

      charts.forEach(function (chartData) {
        var points = chartData.pts || [];
        var col = chartData.color || '#39FF14';
        var safras = points.map(function (point) { return point.safra; });
        var values = points.map(function (point) { return point.valor; });
        if (!values.length) return;

        var curr = values[values.length - 1] || 0;
        var prev = values.length >= 2 ? values[values.length - 2] : null;
        var yearSpan = points.length >= 2 ? points[points.length - 1].anoNum - points[0].anoNum : 0;
        var old5 = points.length >= 2 && yearSpan > 1 ? values[0] : null;
        var var1 = prev && prev !== 0 ? ((curr - prev) / prev) * 100 : null;
        var var5 = old5 && old5 !== 0 ? ((curr - old5) / old5) * 100 : null;
        var isLastNeg = var1 !== null && var1 < 0;
        var subtitleParts = [];

        if (var1 !== null) subtitleParts.push('1 ano: ' + fmtPct(var1));
        if (var5 !== null) subtitleParts.push(yearSpan + ' anos: ' + fmtPct(var5));

        var subtitleLine = subtitleParts.length
          ? '  •  ' + subtitleParts.join('  •  ') + '  •  '
          : '';

        var f1 = isMobile ? 11 : 13, f2 = isMobile ? 13 : 16, f3 = isMobile ? 10 : 12;
        var fs1 = isMobile ? 7 : 8, fs2 = isMobile ? 9 : 10;

        var titleHtml = '<div style="display:flex;flex-direction:column;align-items:center;gap:1px;line-height:1.2;text-align:center;">' +
          '<span style="font-size:' + f1 + 'px;font-weight:700;color:#e0e0e0;">' + escapeHtml(chartData.nomeMercado) + ' - ' + escapeHtml(chartData.atributo) + '</span>' +
          '<span style="font-size:' + f2 + 'px;font-weight:600;color:#ffffff;">' + escapeHtml(chartData.local) + ' <span style="font-size:' + f3 + 'px;font-weight:400;">(' + escapeHtml(chartData.unidadeTexto || unitLabel(chartData.unit)) + ')</span></span>' +
          (chartData.linhaExtra ? '<span style="font-size:' + f3 + 'px;font-weight:600;color:#cfcfcf;">' + escapeHtml(chartData.linhaExtra) + '</span>' : '') +
          '</div>';

        var options = {
          chart: {
            type: 'areaspline',
            backgroundColor: '#0d0d0d',
            height: isMobile ? ${mobileChartHeight} : ${chartHeight},
            spacing: [22, 6, 8, 6],
            style: { fontFamily: 'Arial,sans-serif' }
          },
          title: {
            text: titleHtml,
            useHTML: true,
            align: 'center',
            y: 4
          },
          subtitle: {
            text: '<div style="text-align:center;margin-bottom:1px;"><span style="font-size:' + fs1 + 'px;color:#ffffff;">Fonte: ' + escapeHtml(chartData.fonte || 'RURAL BUSINESS/USDA') + '</span></div>' +
            (subtitleLine ? '<div style="text-align:center;margin-top:6px;"><span style="font-size:' + fs2 + 'px;font-weight:600;color:#ffffff;">' + subtitleLine + '</span></div>' : ''),
            useHTML: true,
            align: 'center',
            y: 42 + (chartData.linhaExtra ? (isMobile ? 14 : 16) : 0)
          },
          xAxis: { categories: safras, visible: false },
          yAxis: { visible: false, min: Math.min.apply(null, values) * 0.85 },
          legend: { enabled: false },
          tooltip: { enabled: false },
          plotOptions: {
            areaspline: {
              fillColor: {
                linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                stops: [[0, hexToRgba(col, 0.35)], [1, hexToRgba(col, 0.02)]]
              },
              lineWidth: 2,
              lineColor: col,
              marker: { enabled: true, radius: 4, fillColor: col, lineWidth: 0 },
              dataLabels: {
                enabled: true,
                useHTML: true,
                allowOverlap: true,
                crop: false,
                overflow: 'allow',
                y: -8,
                style: { textOutline: 'none' },
                formatter: function () {
                  var idx = this.point.index;
                  var isLast = idx === values.length - 1;
                  var ponto = points[idx] || {};
                  var formattedValue = ponto.rotulo != null ? escapeHtml(ponto.rotulo) : fmtVal(this.y, chartData.unit);
                  var safra = escapeHtml(safras[idx]);

                  if (isLast) {
                    var bg = isLastNeg ? 'rgba(192,57,43,0.75)' : 'rgba(39,174,96,0.75)';
                    return '<div style="text-align:center;background:' + bg + ';color:#fff;padding:2px 5px;border-radius:3px;font-size:11px;line-height:1.2;"><b>' + safra + '</b><br/>' + formattedValue + '</div>';
                  }

                  return '<div style="text-align:center;color:#ffffff;font-size:10px;line-height:1.2;"><b>' + safra + '</b><br/>' + formattedValue + '</div>';
                }
              }
            }
          },
          series: [{ data: values, color: col }],
          credits: { enabled: false }
        };

        renderedCharts.push(Highcharts.chart(chartData.id, options));
      });

      var resizeTimer;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          renderedCharts.forEach(function (c) { if (c && c.reflow) c.reflow(); });
        }, 150);
      });
    }());
  </script>
</body>
</html>`;

  const encodedDocument = encodeURIComponent(embedDocument);

  return `<iframe src="data:text/html;charset=utf-8,${encodedDocument}" width="100%" height="${iframeHeight}" frameborder="0" scrolling="yes" style="border:none;display:block;width:100%;max-width:${iframeWidth}px;overflow:auto;"></iframe>`;
}
